import React, { memo, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Button, Icons, Input, Select, SpinnerOverlay } from '@components/common';

import { useManageCellOptions } from '@hooks/useManageCellOptions';

import { isEmpty } from '@utils/common';
import { FREQ_MAX, FREQ_MIN, T5GConverter } from '@utils/frequencyConverters/T5GConverter';
import { removeSpecialCharacters } from '@utils/removeSpecialCharacters';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { regExpDecimal, regExpDecimalOnlyThousandths } from '@constants/regExp';

import { EEditFrequencyBandLabels, EEditFrequencyBandModalFields, IEditFrequencyBand, schema } from './getSchema';

import styles from './EditFrequencyBandModal.module.scss';

export interface IEditFrequencyBandModalProps {
	positiveHandler: (values: IEditFrequencyBand) => void;
	negativeHandler: () => void;
	defaultValues: {
		nrArfcn?: string | number | null | undefined;
		frequency?: string | number | null;
		nrBand?: string | null;
	};
}

function EditFrequencyBandModal({
	defaultValues,
	positiveHandler,
	negativeHandler,
}: IEditFrequencyBandModalProps) {
	const { band5GOptions, isLoadingTechnologiesOptions } = useManageCellOptions();

	const defaultNrBand = useMemo(() => {
		return band5GOptions?.find((option) => {
			return option.value === defaultValues.nrBand;
		});
	}, [band5GOptions, defaultValues.nrBand]);

	const defaultBandsOptions = defaultValues.frequency
		? band5GOptions?.filter((option) => {
			return !defaultValues?.frequency ? true : +option.frequencyRange.from <= +defaultValues.frequency
				&& +option.frequencyRange.to >= +defaultValues.frequency;
		})
		: band5GOptions;

	const [bandsOptions, setBandsOptions] = useState(defaultBandsOptions);

	const {
		handleSubmit,
		register,
		control,
		setValue,
		watch,
		formState: { errors },
	} = useForm<IEditFrequencyBand>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			nrArfcn: defaultValues.nrArfcn,
			frequency: defaultValues.frequency,
			nrBand: defaultNrBand,
		},
	});

	useEffect(() => {
		if (band5GOptions.length && !!defaultNrBand) {
			setValue(EEditFrequencyBandModalFields.nrBand, defaultNrBand, { shouldValidate: true });
		}
	}, [band5GOptions.length, defaultNrBand, setValue]);

	const onSubmit = (values: IEditFrequencyBand): void => {
		positiveHandler(values);
	};

	const onCansel = () => {
		negativeHandler();
	};

	const handleFrequencyChange = (value: string) => {
		const formattedValue = !regExpDecimalOnlyThousandths.test(value) ? value.slice(0, -1) : value;

		removeSpecialCharacters(formattedValue, EEditFrequencyBandModalFields.frequency, setValue, regExpDecimal);

		const parameterNameValue = T5GConverter.freqToNrarfcn(Number(value));

		if (isNaN(parameterNameValue)) setValue(EEditFrequencyBandModalFields.nrArfcn, undefined);
		else setValue(EEditFrequencyBandModalFields.nrArfcn, parameterNameValue, { shouldValidate: true });

		const bands = band5GOptions?.filter((option) => {
			return !watch(EEditFrequencyBandModalFields.frequency)
				|| +option.frequencyRange.from <= +value && +option.frequencyRange.to >= +value;
		});

		setBandsOptions(bands);

		const selectedBand = bands?.find((option) => {
			return option.value === watch(EEditFrequencyBandModalFields.nrBand)?.value;
		});

		if (!selectedBand) {
			setValue(EEditFrequencyBandModalFields.nrBand, null, { shouldValidate: true });
		}
	};

	const handleParameterNameChange = (value: string) => {
		const formattedValue = !regExpDecimalOnlyThousandths.test(value) ? value.slice(0, -1) : value;

		removeSpecialCharacters(formattedValue, EEditFrequencyBandModalFields.nrArfcn, setValue, regExpDecimal);

		const frequencyValue = T5GConverter.nrarfcnToFreq(Number(value));

		if (isNaN(frequencyValue)) {
			setValue(EEditFrequencyBandModalFields.frequency, null, { shouldValidate: true });
		} else setValue(EEditFrequencyBandModalFields.frequency, frequencyValue, { shouldValidate: true });

		const bands = watch(EEditFrequencyBandModalFields.frequency)
			? band5GOptions?.filter((option) => {
				return !watch(EEditFrequencyBandModalFields.frequency) || +option.frequencyRange.from <= +frequencyValue
					&& +option.frequencyRange.to >= +frequencyValue;
			})
			: [];

		setBandsOptions(bands);

		const selectedBand = bands?.find((option) => {
			return option.value === watch(EEditFrequencyBandModalFields.nrBand)?.value;
		});

		if (!selectedBand) {
			setValue(EEditFrequencyBandModalFields.nrBand, null, { shouldValidate: true });
		}
	};

	return (
		<div>
			<p>
				Сalculate the value of the ‘{
				EEditFrequencyBandLabels.nrArfcn
			}’ field based on specified frequency. Available frequency values: from {
				FREQ_MIN
			} to {FREQ_MAX} MHz.
			</p>
			<div className={styles.inputsContainer}>
				<Input
					errorMessage={errors?.frequency?.message}
					label={EEditFrequencyBandLabels.frequency}
					name={EEditFrequencyBandModalFields.frequency}
					onChange={handleFrequencyChange}
					placeholder='Enter frequency'
					register={register}
				/>
				<div className={styles.linkIcon}>
					{Icons.interlink()}
				</div>
				<Input
					errorMessage={errors[EEditFrequencyBandModalFields.nrArfcn]?.message}
					isError={!!errors[EEditFrequencyBandModalFields.nrArfcn]?.message}
					label={EEditFrequencyBandLabels.nrArfcn}
					name={EEditFrequencyBandModalFields.nrArfcn}
					onChange={handleParameterNameChange}
					placeholder={`Enter ${EEditFrequencyBandLabels.nrArfcn}`}
					register={register}
					required
				/>
			</div>
			<div className='display-flex flex-row mt-20'>
				<Select
					control={control}
					disabled={!watch(EEditFrequencyBandModalFields.nrArfcn)}
					errorMessage={(errors[EEditFrequencyBandModalFields.nrBand] as any)?.message}
					isError={!!errors[EEditFrequencyBandModalFields.nrBand]?.message}
					label={EEditFrequencyBandLabels.nrBand}
					maxWidth='202px'
					minWidth='202px'
					name='nrBand'
					options={bandsOptions}
					placeholder={`Select ${EEditFrequencyBandLabels.nrBand}`}
					required
					width='202px'
				/>
			</div>
			<div className='display-flex flex-row justify-end flex-gap_20 mt-30'>
				<Button
					className='f5gButton f5gButton_primary'
					onClick={onCansel}
					withoutBackground
				>
					Cancel
				</Button>
				<Button
					className='f5gButton f5gButton_primary'
					disabled={!isEmpty(errors)}
					onClick={handleSubmit(onSubmit)}
				>
					Submit
				</Button>
			</div>
			{isLoadingTechnologiesOptions && <SpinnerOverlay />}
		</div>
	);
}

export default memo(EditFrequencyBandModal);
